export type PastaDetailOrigin = 'dashboard' | 'pastas' | 'kanban';

export interface PastaDetailReturnTarget {
  from: '/habita' | '/habita/pastas' | '/habita/kanban';
  label: 'Dashboard' | 'Pastas' | 'Kanban';
}

export interface PastaDetailExternalIds {
  chatwootContactId?: string | null;
  crmContactId?: string | null;
}

export interface PastaDetailNavigationState {
  pastaDetailExternalIds?: PastaDetailExternalIds;
  pastaDetailReturn?: PastaDetailReturnTarget;
}

interface ResolvedPastaDetailNavigation {
  source: 'state' | 'session' | 'default';
  target: PastaDetailReturnTarget;
}

type ExternalIdSource = {
  id?: string | number;
  crm_contact_id?: string | null;
  chatwoot_contact_id?: string | number | null;
};

const storageKeyPrefix = 'habita4u:pasta-detail-return:';
const externalIdsStorageKeyPrefix = 'habita4u:pasta-detail-external-ids:';

const returnTargets: Record<PastaDetailOrigin, PastaDetailReturnTarget> = {
  dashboard: { from: '/habita', label: 'Dashboard' },
  pastas: { from: '/habita/pastas', label: 'Pastas' },
  kanban: { from: '/habita/kanban', label: 'Kanban' },
};

const defaultTarget = returnTargets.pastas;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isReturnTarget(value: unknown): value is PastaDetailReturnTarget {
  if (!isObject(value)) return false;

  return Object.values(returnTargets).some(
    (target) => value.from === target.from && value.label === target.label
  );
}

function resolveLegacyState(value: unknown): PastaDetailReturnTarget | null {
  if (!isObject(value)) return null;

  if (value.from === '/pastas') return returnTargets.pastas;
  if (value.from === '/dashboard') return returnTargets.dashboard;
  if (value.from === '/kanban') return returnTargets.kanban;

  return Object.values(returnTargets).find((target) => value.from === target.from) ?? null;
}

function optionalExternalId(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text && text !== '-' ? text : null;
}

export function createPastaDetailPath(item: { id: string | number }): string {
  return `/habita/cliente/${encodeURIComponent(String(item.id))}`;
}

export function pastaDetailStorageIdFromItem(item: { id: string | number }): string {
  return String(item.id);
}

function externalIdsFromItem(item?: ExternalIdSource): PastaDetailExternalIds | undefined {
  if (!item) return undefined;

  const externalIds: PastaDetailExternalIds = {
    chatwootContactId: optionalExternalId(item.chatwoot_contact_id),
    crmContactId: optionalExternalId(item.crm_contact_id),
  };

  return Object.values(externalIds).some(Boolean) ? externalIds : undefined;
}

export function rememberPastaDetailExternalIdsFromItem(item?: ExternalIdSource): void {
  if (!item || item.id === undefined) return;

  const id = optionalExternalId(item.id);
  if (!id) return;

  rememberPastaDetailExternalIds(id, externalIdsFromItem(item) ?? null);
}

function normalizeExternalIds(value: unknown): PastaDetailExternalIds | null {
  if (!isObject(value)) return null;

  const externalIds: PastaDetailExternalIds = {
    chatwootContactId: optionalExternalId(value.chatwootContactId),
    crmContactId: optionalExternalId(value.crmContactId),
  };

  return Object.values(externalIds).some(Boolean) ? externalIds : null;
}

function getStorageKey(pastaId: string): string {
  return `${storageKeyPrefix}${pastaId}`;
}

function getExternalIdsStorageKey(pastaId: string): string {
  return `${externalIdsStorageKeyPrefix}${pastaId}`;
}

function readSessionTarget(pastaId: string | undefined): PastaDetailReturnTarget | null {
  if (!pastaId || typeof window === 'undefined') return null;

  try {
    const storedValue = window.sessionStorage.getItem(getStorageKey(pastaId));
    if (!storedValue) return null;

    const parsedValue: unknown = JSON.parse(storedValue);
    return isReturnTarget(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
}

function readSessionExternalIds(pastaId: string | undefined): PastaDetailExternalIds | null {
  if (!pastaId || typeof window === 'undefined') return null;

  try {
    const storedValue = window.sessionStorage.getItem(getExternalIdsStorageKey(pastaId));
    if (!storedValue) return null;

    return normalizeExternalIds(JSON.parse(storedValue));
  } catch {
    return null;
  }
}

export function createPastaDetailNavigationState(
  origin: PastaDetailOrigin,
  item?: ExternalIdSource
): PastaDetailNavigationState {
  const externalIds = externalIdsFromItem(item);

  return {
    ...(externalIds ? { pastaDetailExternalIds: externalIds } : {}),
    pastaDetailReturn: returnTargets[origin],
  };
}

export function resolvePastaDetailNavigation(
  pastaId: string | undefined,
  locationState: unknown
): ResolvedPastaDetailNavigation {
  if (isObject(locationState) && isReturnTarget(locationState.pastaDetailReturn)) {
    return { source: 'state', target: locationState.pastaDetailReturn };
  }

  const legacyTarget = resolveLegacyState(locationState);
  if (legacyTarget) return { source: 'state', target: legacyTarget };

  const sessionTarget = readSessionTarget(pastaId);
  if (sessionTarget) return { source: 'session', target: sessionTarget };

  return { source: 'default', target: defaultTarget };
}

export function resolvePastaDetailExternalIds(
  pastaId: string | undefined,
  locationState: unknown
): PastaDetailExternalIds | null {
  if (isObject(locationState)) {
    const stateExternalIds = normalizeExternalIds(locationState.pastaDetailExternalIds);
    if (stateExternalIds) return stateExternalIds;
  }

  return readSessionExternalIds(pastaId);
}

export function rememberPastaDetailNavigation(
  pastaId: string | undefined,
  target: PastaDetailReturnTarget
): void {
  if (!pastaId || typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(getStorageKey(pastaId), JSON.stringify(target));
  } catch {
    // Navigation state is a convenience fallback; failing silently keeps routing usable.
  }
}

export function rememberPastaDetailExternalIds(
  pastaId: string | undefined,
  externalIds: PastaDetailExternalIds | null
): void {
  if (!pastaId || typeof window === 'undefined') return;
  const normalizedExternalIds = normalizeExternalIds(externalIds);

  try {
    if (normalizedExternalIds) {
      window.sessionStorage.setItem(getExternalIdsStorageKey(pastaId), JSON.stringify(normalizedExternalIds));
    } else {
      window.sessionStorage.removeItem(getExternalIdsStorageKey(pastaId));
    }
  } catch {
    // Navigation state is a convenience fallback; failing silently keeps routing usable.
  }
}
